import {useState, useEffect} from 'react';
import {Link} from 'react-router';
import {UilTrash} from '@iconscout/react-unicons';
import AccountDataEditor from '../components/AccountDataEditor';
import ConfirmModal from '../components/ConfirmModal';
import DisplayModal from '../components/DisplayModal';
import AccountPhoneEditor from '../components/AccountPhoneEditor';
import TermsAndConditions from '../components/TermsAndConditions';
import DentalBookPrivacyPolicy from '../components/DentalBookPrivacyPolicy';
import HippaNoticeOfPrivacy from '../components/HippaNoticeOfPrivacy';
import SocialAccountsSection from '../components/SocialAccountsSection';

import {useAuth} from '../hooks/AuthContext';
import * as AccountApi from '../api/AccountApi';
import PageShell from '../components/PageShell';
import {problemMessage} from '../api/client';

export default function Account() {
  const auth = useAuth();
  const [termsConditionsShow, setTermsConditionsShow] = useState(false);
  const [privacyPloicyShow, setPrivacyPloicyShow] = useState(false);
  const [accountData, setAccountData] = useState(auth.user);
  const [deleteConfirmShow, setDeleteConfirmShow] = useState(false);

  const handleChangeForm = (e) => {
    const {name, value} = e.target;
    setAccountData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handlePhone = (phoneNumber) => {
    setAccountData((prevState) => ({
      ...prevState,
      phone: phoneNumber,
    }));
  };

  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const uploadAccountData = async () => {
    setSaveError(null);
    setSaved(false);
    try {
      await auth.updateAccountData(accountData);
      setSaved(true);
    } catch (err) {
      // Show what the server rejected and drop the unsaved edit instead of displaying it as saved.
      setSaveError(problemMessage(err, 'Could not save your profile. Please try again.'));
      setAccountData(auth.user);
    }
  };

  const deleteAccount = async () => {
    setDeleteError(null);
    try {
      await AccountApi.deleteAccount();
    } catch (err) {
      // Close the confirmation (its button is left spinning otherwise) and say why nothing happened.
      setDeleteConfirmShow(false);
      setDeleteError(problemMessage(err, 'Could not delete your account. Please try again.'));
      return;
    }
    auth.logout();
  };

  useEffect(() => {
    setAccountData(auth.user);
  }, [auth.user]);

  return (
    <PageShell title="My Profile" showBackButton={true}>
      <div className="profile-main">
        <div className="profile-row">
          <div className="profile-col-a">
            <h4>Customer Information</h4>
            <div className="profile-col-card">
              <ul className="list-style-none">
                <li>
                  <div className="profile-item fix">
                    <div className="profile-name">Customer Level</div>
                    <div className="profile-info">
                      <span>{accountData.customer_level}</span>
                    </div>
                    <Link to="/membership" className="upgrade-sm">
                      Membership Upgrade
                    </Link>
                  </div>
                </li>
                <li>
                  <div className="profile-item">
                    <div className="profile-name">Current Points</div>
                    <div className="profile-info">
                      <span>{accountData.points}</span>
                    </div>
                  </div>
                </li>
                <li>
                  <div className="profile-item">
                    <div className="profile-name">Points Expiration Date</div>
                    <div className="profile-info">
                      <span>{accountData.points_expired_at}</span>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="profile-col-a">
            <h4>Profile</h4>
            {saveError && (
              <p role="alert" className="text-danger" style={{whiteSpace: 'pre-line'}}>
                {saveError}
              </p>
            )}
            {saved && <p role="status">Profile saved.</p>}
            {deleteError && (
              <p role="alert" className="text-danger" style={{whiteSpace: 'pre-line'}}>
                {deleteError}
              </p>
            )}
            <div className="profile-col-card">
              <ul className="list-style-none">
                <li>
                  <div className="profile-item">
                    <div className="profile-name">Email</div>
                    <div className="profile-info">
                      <span id="email">{accountData.email}</span>
                    </div>
                  </div>
                </li>
                <li>
                  <AccountDataEditor
                    title="Name"
                    name="username"
                    value={accountData.username}
                    handleChangeForm={handleChangeForm}
                    updateAccountData={uploadAccountData}
                  />
                </li>
                <li>
                  <AccountPhoneEditor
                    title="Phone"
                    name="phone"
                    value={accountData.phone || ''}
                    handlePhone={handlePhone}
                    updateAccountData={uploadAccountData}
                  />
                </li>
              </ul>
            </div>
          </div>
          <div className="profile-col-a">
            <h4>Sign-in methods</h4>
            <SocialAccountsSection />
          </div>
          <div className="profile-col-a">
            <h4>Service</h4>
            <div className="profile-col-card">
              <ul className="list-style-none">
                <li>
                  <div className="profile-item">
                    <div>
                      <p style={{marginBottom: '0'}}>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          style={{
                            fontSize: '14px',
                            verticalAlign: 'baseline',
                            lineHeight: 'inherit',
                            border: 0,
                          }}
                          onClick={() => setTermsConditionsShow(true)}
                        >
                          Terms of Conditions
                        </button>
                        <span>&nbsp;&&nbsp;</span>
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          style={{
                            fontSize: '14px',
                            verticalAlign: 'baseline',
                            lineHeight: 'inherit',
                            border: 0,
                          }}
                          onClick={() => setPrivacyPloicyShow(true)}
                        >
                          Privacy Policy
                        </button>
                      </p>
                    </div>
                  </div>
                </li>
                <li>
                  <div className="profile-item">
                    <div className="profile-name">Customer Service Email</div>
                    <div className="profile-info">
                      <a href="mailto:service@askin.com.tw">service@askin.com.tw</a>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="profile-col-a">
            <h4>Account Actions</h4>
            <div className="profile-col-card">
              <ul className="list-style-none">
                <li>
                  <div className="profile-item">
                    <div className="profile-name">Log out of your account</div>
                    <div className="profile-info">
                      <button
                        onClick={() => {
                          auth.logout();
                        }}
                        style={{
                          padding: '6px 16px',
                          borderRadius: '20px',
                          border: '1px solid #C43B71',
                          background: 'transparent',
                          color: '#C43B71',
                          cursor: 'pointer',
                          fontWeight: '600',
                        }}
                      >
                        Log Out
                      </button>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="profile-col-a">
            <h4>Delete Data</h4>
            <div className="profile-col-card">
              <ul className="list-style-none">
                <li>
                  <div className="profile-item">
                    <div className="profile-name">Delete Account and Data</div>
                    <div className="profile-info">
                      <button
                        type="button"
                        className="icon-btn"
                        style={{padding: 0, display: 'flex'}}
                        aria-label="Delete account and data"
                        onClick={() => setDeleteConfirmShow(true)}
                      >
                        <UilTrash aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <ConfirmModal
          modalTitle="Delete Accocunt"
          show={deleteConfirmShow}
          btnText="Delete"
          onHide={() => setDeleteConfirmShow(false)}
          clickBtn={() => deleteAccount()}
        >
          <p style={{fontSize: '1.2rem'}}>
            This process will delete all data including all uploaded X-Rays and Photos. Please make sure you
            have made all necessary backup, we will not be able to retrieve your data.
          </p>
        </ConfirmModal>
        <DisplayModal
          show={termsConditionsShow}
          onHide={() => setTermsConditionsShow(false)}
          title={'Terms and Conditions of Usage of ' + 'our Website and Mobile Application'}
        >
          <div style={{height: '65vh', overflowX: 'auto'}}>
            <TermsAndConditions />
          </div>
        </DisplayModal>
        <DisplayModal
          show={privacyPloicyShow}
          onHide={() => setPrivacyPloicyShow(false)}
          title="Web And Mobile Privacy Policy"
        >
          <div style={{height: '65vh', overflowX: 'auto'}}>
            <DentalBookPrivacyPolicy />
            <HippaNoticeOfPrivacy />
          </div>
        </DisplayModal>
      </div>
    </PageShell>
  );
}
